import type { TruckScheduleEntry } from '@chukua/common';
import { Button, Tag } from 'antd';
import { TriangleAlert, Truck, Wrench } from 'lucide-react';
import clsx from 'clsx';
import { formatWindow } from '../policies/booking-labels';
import type { BookingRef } from '../hooks/use-schedule-dialogs';

interface TruckScheduleListProps {
  entries: TruckScheduleEntry[];
  /** Null when the viewer cannot book: the rows are then read-only. */
  onMove: ((booking: BookingRef) => void) | null;
  onCancel: ((booking: BookingRef) => void) | null;
}

const toRef = (entry: TruckScheduleEntry): BookingRef => ({
  id: entry.id,
  workshopName: entry.label,
  startsAt: entry.startsAt,
  endsAt: entry.endsAt ?? entry.startsAt,
});

/** The truck's bookings and its trip, in time order. A booking over a trip, and the trip it runs over, carry a warning in words as well as colour. */
export function TruckScheduleList({ entries, onMove, onCancel }: TruckScheduleListProps) {
  return (
    <ol className="m-0 flex list-none flex-col gap-3 p-0" aria-label="Schedule">
      {entries.map((entry) => {
        const Icon = entry.kind === 'booking' ? Wrench : Truck;
        return (
          <li
            key={`${entry.kind}-${entry.id}`}
            className={clsx(
              'flex flex-wrap items-center justify-between gap-3 rounded-lg border bg-surface px-4 py-3.5',
              entry.hasConflict ? 'border-warning' : 'border-line',
            )}
          >
            <div className="flex items-center gap-3">
              <Icon className="size-4 text-muted" aria-hidden />
              <div className="flex flex-col">
                <span className="font-medium text-fg">{entry.kind === 'booking' ? `Workshop: ${entry.label}` : `Trip ${entry.label}`}</span>
                <span className="text-sm text-muted">{formatWindow(entry.startsAt, entry.endsAt)}</span>
              </div>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              {entry.hasConflict && (
                <Tag color="warning" icon={<TriangleAlert className="mr-1 inline size-3.5" aria-hidden />}>
                  {entry.kind === 'booking' ? 'Overlaps a trip' : 'Overlaps a workshop booking'}
                </Tag>
              )}
              {entry.kind === 'booking' && onMove && (
                <Button size="small" onClick={() => onMove(toRef(entry))}>
                  Move
                </Button>
              )}
              {entry.kind === 'booking' && onCancel && (
                <Button size="small" danger onClick={() => onCancel(toRef(entry))}>
                  Cancel
                </Button>
              )}
            </div>
          </li>
        );
      })}
    </ol>
  );
}
